import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const EnquiryContext = createContext(null);
const KEY = "kp_enquiry_list_v1";

// Only public, customer-safe fields are ever stored — never source cells / notes / costs.
const pick = (p, qty) => ({
  id: p.id,
  code: p.code || "",
  name: p.name,
  brand: p.brand || "",
  category: p.category || "",
  subcategory: p.subcategory || "",
  price: p.price || 0,
  price_on_request: !!p.price_on_request,
  warranty_display: p.warranty_display || "",
  condition: p.condition || "",
  excludes: p.excludes || "",
  qty: Math.max(1, qty || 1),
});

export function EnquiryProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      console.debug("enquiry list persist failed", e);
    }
  }, [items]);

  // One-time refresh: re-sync saved selections with current public catalogue data
  // (name, caveats, condition, warranty, price) by product ID, keeping each selected
  // ID and its requested quantity. Items no longer in the catalogue are left untouched.
  useEffect(() => {
    let cancelled = false;
    let saved = [];
    try {
      saved = JSON.parse(localStorage.getItem(KEY) || "[]");
    } catch {
      saved = [];
    }
    if (!saved.length) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        if (cancelled || !Array.isArray(list)) return;
        const byId = new Map(list.map((p) => [p.id, p]));
        setItems((prev) => prev.map((it) => {
          const p = byId.get(it.id);
          return p ? pick(p, it.qty) : it;
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const add = useCallback((p, qty = 1) => {
    setItems((prev) => {
      if (prev.some((x) => x.id === p.id)) return prev;
      return [...prev, pick(p, qty)];
    });
  }, []);

  const addMany = useCallback((list) => {
    setItems((prev) => {
      const have = new Set(prev.map((x) => x.id));
      const extra = list.filter((p) => !have.has(p.id)).map((p) => pick(p, p.qtyRequested || 1));
      return [...prev, ...extra];
    });
  }, []);

  const remove = useCallback((id) => setItems((prev) => prev.filter((x) => x.id !== id)), []);
  const setQty = useCallback(
    (id, qty) => setItems((prev) => prev.map((x) => (x.id === id ? { ...x, qty: Math.max(1, qty || 1) } : x))),
    []
  );
  const clear = useCallback(() => setItems([]), []);
  const has = useCallback((id) => items.some((x) => x.id === id), [items]);

  return (
    <EnquiryContext.Provider value={{ items, add, addMany, remove, setQty, clear, has, count: items.length }}>
      {children}
    </EnquiryContext.Provider>
  );
}

export function useEnquiry() {
  const ctx = useContext(EnquiryContext);
  if (!ctx) throw new Error("useEnquiry must be used within EnquiryProvider");
  return ctx;
}
